import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Clock, CheckCircle2, Users, Calendar, Trophy, BookOpen, Heart, MessageCircle, Play, TrendingUp, Zap, Timer, Youtube, User, LayoutGrid, HelpCircle, Layers, Bell, Check, Infinity } from "lucide-react";
import { Link } from "react-router-dom";

const IndexJp = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="CSW - オンライン自習室 | 無料バーチャル勉強部屋 | Online Workspace | Productivity Tool | Study Stream"
        description="無料のオンライン自習室で集中力と学習習慣を高めましょう。24時間利用可能、ポモドーロタイマー、世界中の学生とつながる。"
        keywords="オンライン自習室, バーチャル勉強部屋, オンライン勉強, 勉強配信, ポモドーロタイマー"
        canonicalUrl="/jp/"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
          </div>

          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
              {/* Left Content */}
              <div className="space-y-8 animate-fade-in">
                <div className="space-y-4">
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-foreground leading-tight">
                    オンライン自習室
                  </h1>
                  <p className="text-2xl sm:text-3xl text-muted-foreground font-light">
                    集中力と学習習慣を高める空間
                  </p>
                </div>

                <p className="text-lg text-muted-foreground leading-relaxed">
                  オンライン自習室、特にCSW（Chill Study Work）は、私の勉強方法を大きく変えてくれたツールです。自宅では集中しにくく、時間管理も難しかったのですが、オンライン自習室の機能とルールのおかげで、学習効率が目に見えて向上しました。
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    asChild
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 transition-all hover:scale-105"
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      今すぐ参加する
                    </a>
                  </Button>
                  <Button 
                    asChild
                    variant="outline"
                    size="lg"
                    className="font-semibold px-8 group"
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                      <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                      体験してみる
                    </a>
                  </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-8 pt-4">
                  <div>
                    <div className="text-4xl font-bold text-foreground">100k+</div>
                    <div className="text-muted-foreground mt-1">学習時間達成</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-foreground">8k+</div>
                    <div className="text-muted-foreground mt-1">タスク完了</div>
                  </div>
                </div>
              </div>

              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: "0.2s" }}>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border-2 border-border/50 bg-card/50 backdrop-blur-sm">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 opacity-50" />
                  <video 
                    autoPlay 
                    loop 
                    muted 
                    playsInline
                    className="w-full h-auto relative z-10"
                  >
                    <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                  </video>
                </div>
                {/* Floating Stats Cards */}
                <div className="absolute -bottom-6 -left-6 bg-card/90 backdrop-blur-sm border-2 border-border/50 rounded-xl p-4 shadow-lg animate-fade-in" style={{ animationDelay: "0.4s" }}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">生産性向上</div>
                      <div className="text-xl font-bold text-foreground">+40%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Modes Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">新しい人との出会い</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                コミュニケーションの負担なく
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                プレッシャーを感じることなく、自然に繋がれる環境です。
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Chill Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Heart className="w-8 h-8 text-blue-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Chill</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  リラックスした雰囲気でゆったりと学習できます。
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    BGM付き
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    自由なチャット
                  </li>
                </ul>
              </Card>

              {/* Study Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 border-primary hover:border-primary transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Study</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  集中した学習環境を体験できます。
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    ポモドーロタイマー
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    学習記録
                  </li>
                </ul>
              </Card>

              {/* Work Mode */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-purple-500/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Zap className="w-8 h-8 text-purple-500" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">Work</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  最大限の生産性を発揮できる空間です。
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    タスク管理
                  </li>
                  <li className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    成果追跡
                  </li>
                </ul>
              </Card>
            </div>

            <div className="text-center mt-12">
              <Button 
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8"
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  始めてみよう！
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                オンライン自習室の基本機能
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                CSWは学習効率を高める快適な環境を提供します。学習の進捗を記録し、振り返ることができる便利な機能とツールを揃えています。
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Feature 1 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BookOpen className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">学習記録と振り返り</h3>
                <p className="text-muted-foreground leading-relaxed">
                  勉強の開始時間と終了時間を記録し、学習時間を視覚的に確認できます。これにより、自分の学習ペースと集中度を簡単に把握できます。
                </p>
              </Card>

              {/* Feature 2 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <MessageCircle className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">自由な自習空間と専用チャット</h3>
                <p className="text-muted-foreground leading-relaxed">
                  誰でも自由に利用できる自習空間で、リアクションやチャットを通じて他のユーザーと交流できます。このような交流は学習モチベーションの維持に大きく役立ちます。
                </p>
              </Card>

              {/* Feature 3 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all hover:scale-105 group">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Trophy className="w-8 h-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-4">モチベーションシステム</h3>
                <p className="text-muted-foreground leading-relaxed">
                  勉強した時間や達成した課題に応じて報酬を受け取ることができ、楽しみながら学習を進められます。また、集中力を維持するための様々なプログラムが用意されています。
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Alone but Never Lonely Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/5 to-background" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto items-center">
              <Card className="p-12 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <h2 className="text-4xl font-bold text-foreground mb-4">一人でも、孤独じゃない</h2>
                <p className="text-xl text-muted-foreground">
                  内向的な方にも特に適した環境で、静かに勉強しながら自然に人々と繋がることができます。
                </p>
              </Card>

              <Card className="p-12 bg-card/50 backdrop-blur-sm border-2 hover:border-primary/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <Heart className="w-12 h-12 text-primary" />
                  <h2 className="text-4xl font-bold text-foreground">静かな時間</h2>
                </div>
                <p className="text-xl text-muted-foreground">
                  集中が必要な瞬間に、静かな環境を提供します。
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Impact Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4">
              <div className="inline-block px-4 py-2 bg-primary/10 rounded-full mb-4">
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">効果的な学習</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
                学習習慣とモチベーションの向上
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                CSWを使うことで、特に学習習慣の形成に大きな効果を実感しました。
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Impact 1 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Calendar className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">生活習慣の形成</h3>
                <p className="text-muted-foreground">
                  毎日決まった時間に学習を始めることで、日常生活が規則正しくなりました。
                </p>
              </Card>

              {/* Impact 2 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">相互観察による集中力維持</h3>
                <p className="text-muted-foreground">
                  他のユーザーと一緒に勉強することで、緊張感と集中力を維持できました。
                </p>
              </Card>

              {/* Impact 3 */}
              <Card className="p-8 bg-gradient-to-br from-card to-card/50 border-2 hover:border-primary/50 transition-all">
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                  <Clock className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">学習データの蓄積</h3>
                <p className="text-muted-foreground">
                  定期的に自分の学習データを振り返り、目標達成のための計画が立てやすくなりました。
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Features Grid Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted via-background to-background" />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16 space-y-4 animate-fade-in">
              <h2 className="font-display text-4xl sm:text-5xl font-bold">
                <span className="text-gradient">生産性</span>を高める方法
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                最適なモードを選んで、潜在能力を最大限に発揮しましょう
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Users, title: "グループモード", description: "世界中の仲間と一緒に勉強しましょう。バーチャルルームに参加してモチベーションを維持。", gradient: "from-primary/20 to-primary/5", iconColor: "text-primary", href: "/group-study/" },
                { icon: Timer, title: "タイマーモード", description: "内蔵のポモドーロタイマーで集中力を高めましょう。賢く働きましょう。", gradient: "from-secondary/20 to-secondary/5", iconColor: "text-secondary", href: "/study-timer/" },
                { icon: Youtube, title: "YTモード", description: "作業中に教育コンテンツをストリーミング。継続的に学び、成長しましょう。", gradient: "from-accent/20 to-accent/5", iconColor: "text-accent", href: "/study-stream/" },
                { icon: User, title: "ソロモード", description: "個人のワークスペースで集中。プライバシーと生産性の両立。", gradient: "from-muted-foreground/20 to-muted-foreground/5", iconColor: "text-foreground", href: "/online-workspace/" },
                { icon: LayoutGrid, title: "タスク", description: "カンバンボードで作業を整理。ドラッグ＆ドロップでToDoを征服。", gradient: "from-primary/20 to-primary/5", iconColor: "text-primary", href: "/task-management/" },
                { icon: HelpCircle, title: "クイズ", description: "インタラクティブなクイズで知識をテスト。より賢く学びましょう。", gradient: "from-secondary/20 to-secondary/5", iconColor: "text-secondary", href: "/quizzes/" },
                { icon: Layers, title: "フラッシュカード", description: "デジタルフラッシュカードで科目をマスター。暗記と復習に最適。", gradient: "from-accent/20 to-accent/5", iconColor: "text-accent", href: "/flash-cards/" },
                { icon: Bell, title: "リマインダー", description: "締め切りを忘れません。スマートリマインダーで学習を軌道に乗せましょう。", gradient: "from-muted-foreground/20 to-muted-foreground/5", iconColor: "text-foreground", href: "/reminders/" },
              ].map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Link key={feature.title} to={feature.href} onClick={() => window.scrollTo(0, 0)}>
                    <Card
                      className="group relative overflow-hidden border-border bg-card hover:bg-card/80 p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl animate-fade-in h-full"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
                      <div className="relative z-10 space-y-4">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className={`h-7 w-7 ${feature.iconColor}`} />
                        </div>
                        <h3 className="text-xl font-bold text-foreground">{feature.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                      </div>
                      <div className="absolute -bottom-2 -right-2 w-24 h-24 bg-primary/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Study Stream Section */}
        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[100px] animate-glow-pulse" />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[100px] animate-glow-pulse" style={{ animationDelay: '1.5s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <Card className="border-2 border-border bg-card/50 backdrop-blur-xl overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-12 p-8 sm:p-12 lg:p-16">
                  <div className="space-y-8 animate-fade-in">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                      <Infinity className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold text-primary">制限なし</span>
                    </div>
                    
                    <div className="space-y-4">
                      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                        スタディ <span className="text-gradient">ストリーム</span>
                      </h2>
                      <p className="text-2xl text-muted-foreground font-light">
                        生産性にお金を払わないで
                      </p>
                    </div>
                    
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      いつでも無制限のストリームに参加できます。バーチャル自習室で世界中の何千人もの学生とつながりましょう。集中し、モチベーションを維持し、生産的に - 完全無料。
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105" asChild>
                        <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">無料ストリーム開始</a>
                      </Button>
                      <Button size="lg" variant="outline" className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105" asChild>
                        <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">詳しく見る</a>
                      </Button>
                    </div>
                  </div>
                  
                  <div className="space-y-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                    <div className="space-y-4">
                      <h3 className="text-2xl font-bold text-foreground mb-6">
                        必要なすべてが、<span className="text-gradient">永久無料</span>
                      </h3>
                      
                      <div className="space-y-3">
                        {["無制限の自習セッション", "24時間すべてのルームにアクセス", "隠れた費用なし", "すべてのストリームに即座に参加", "グローバルコミュニティとつながる", "プレミアム機能込み"].map((benefit, index) => (
                          <div key={benefit} className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors animate-slide-in" style={{ animationDelay: `${0.3 + index * 0.05}s` }}>
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                              <Check className="w-4 h-4 text-primary-foreground" />
                            </div>
                            <span className="text-foreground font-medium">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <Card className="p-6 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <p className="text-3xl font-bold text-gradient mb-1">∞</p>
                          <p className="text-sm text-muted-foreground">利用可能なストリーム</p>
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-gradient mb-1">$0</p>
                          <p className="text-sm text-muted-foreground">費用</p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <Card className="max-w-4xl mx-auto p-12 bg-card/80 backdrop-blur-sm border-2 border-primary/20 text-center">
              <h2 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
                今すぐ始めましょう
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                CSWで効率的な学習習慣を身につけ、目標を達成しましょう。完全無料で利用できます。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 transition-all hover:scale-105">
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">無料で始める</a>
                </Button>
                <Button asChild variant="outline" size="lg" className="font-semibold px-8">
                  <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">詳しく見る</a>
                </Button>
              </div>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default IndexJp;
